import React from 'react';

import { Typography, useTheme } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

import Rating from '~/components/Rating/Rating';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useAvatarColor from '~/hooks/useAvatarColor';
import { CustomThumbDownIcon, CustomThumbUpIcon } from '~/pages/SingleVideoPage/styles';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN, SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';

export enum RatingType {
  Thumb,
  Stars,
}

const CommentMessage = ({ text, name, date, rating = 0, ratingType, learnerId, ...rest }) => {
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();
  const avatarColor = useAvatarColor();
  const isSsku = useIsSsku();

  return (
    <Wrapper data-testid="commentMessageItem" {...rest}>
      <Row>
        {learnerId === userInfo?.id && (
          <ProfileCircle background={avatarColor?.background} color={avatarColor?.color}>
            {userInfo?.name
              ?.split(' ')
              .map((item) => item.substr(0, 1))
              .join('')}
          </ProfileCircle>
        )}
        <div>
          <NameRow>
            <UserName data-testid="commentName">{name}</UserName>
            {ratingType === RatingType.Thumb &&
              (rating > 3 ? (
                <CustomThumbUpIcon
                  data-testid="likedCommentIcon"
                  filled={true}
                  color={theme.palette.common.accentFaded}
                />
              ) : (
                <CustomThumbDownIcon
                  data-testid="unlikedCommentIcon"
                  filled={true}
                  color={theme.palette.common.accentFaded}
                />
              ))}
          </NameRow>
          <Date data-testid="commentDate">{date}</Date>
        </div>
        {ratingType === RatingType.Stars && (
          <StyledRating hideValue name="reviewItem" value={rating} />
        )}
      </Row>
      {text && !isSsku && <Message data-testid="commentMessage">{text}</Message>}
    </Wrapper>
  );
};

export default CommentMessage;

const Message = styled('div')(({ theme }: { theme: Theme }) => ({
  color: theme.palette.common.text.secondaryToPrimary,
  fontSize: 16,
  whiteSpace: 'pre-wrap',
  marginTop: SMALL_MARGIN,
  [theme.breakpoints.up('md')]: {
    fontSize: 18,
  },
}));
const Date = styled(Typography)({
  fontSize: 14,
});
const UserName = styled(Typography)({
  fontSize: 18,
});
const Wrapper = styled('div')`
  margin-bottom: ${MIDDLE_MARGIN_PX};

  &:not(:last-child) {
    padding-bottom: ${MIDDLE_MARGIN_PX};
    border-bottom: 1px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  }
`;
const Row = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
}));
const NameRow = styled('div')`
  display: flex;
  align-items: center;

  & > p:first-of-type {
    margin-right: ${TINY_MARGIN_PX};
  }
`;
const ProfileCircle = styled('button', {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'color',
})<{ background?: string; color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  font-weight: 700;
  font-size: 16px;
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  line-height: 22px;
  margin-right: ${SMALL_MARGIN_PX};
`;
const StyledRating = styled(Rating)`
  margin-left: auto;
`;
